import { AccountsService } from 'base/accounts_service';
import { getCookie } from 'base/api_host';
import { getReferralCode } from 'base/referral';
import { Dialog } from 'base/ui/dialog';
import { LoadingDots } from 'base/ui/loading_dots';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { saveUserJwt } from '../../../library/src/modules/cookies';
import { LoginForm, SignupForm, VerifyEmailForm } from './forms';
import { SharedFormState } from './form_state';

export type AuthFlowDialogProps = {
  signupTitle: string;
  onClose: () => void;
  /* Add the "isCommenter" flag to new signup accounts, so they are excluded from email marketing */
  isCommenter: boolean;
  accountsService: AccountsService;
  disableUnloadConfirmation: () => Promise<void>;
};

export const AuthFlowDialog = observer(function _AuthFlowDialog({
  signupTitle,
  onClose,
  isCommenter,
  accountsService,
  disableUnloadConfirmation,
}: AuthFlowDialogProps) {
  const [formState] = React.useState(() => new SharedFormState());
  const [mode, setMode] = React.useState<
    'signup' | 'login' | 'verify' | 'success'
  >('signup');

  async function gotAccessToken(token: string) {
    saveUserJwt(token);
    setMode('success');
    await disableUnloadConfirmation();
    window.location.reload();
  }

  return (
    <Dialog
      title={
        mode === 'signup'
          ? signupTitle
          : mode === 'login'
          ? 'Log in'
          : mode === 'success'
          ? 'Success'
          : 'Verify your email'
      }
      onClose={mode === 'login' || mode === 'signup' ? onClose : undefined}
    >
      {mode === 'signup' && (
        <SignupForm
          formState={formState}
          goToLogin={() => setMode('login')}
          submitSignupForm={async () => {
            const params = new URLSearchParams(window.location.search);
            await accountsService.signup({
              ...formState,
              signupReferral: getCookie('rtag'),
              isCommenter,
              fbc: getCookie('fbclid'),
              fbp: getCookie('_fbp'),
              gclid: getCookie('gclid'),
              referralCode: getReferralCode(),
            });
            setMode('verify');
          }}
        />
      )}
      {mode === 'login' && (
        <LoginForm
          hideTitle
          formState={formState}
          goToSignup={() => setMode('signup')}
          submitLoginForm={async () => {
            const res = await accountsService.login(formState);
            gotAccessToken(res.accessToken);
          }}
          goToForgotPassword={() => {
            window.open('/app/forgot-password', '_blank');
          }}
        />
      )}
      {mode === 'verify' && (
        <VerifyEmailForm
          hideTitle
          email={formState.email}
          handleVerify={async (code) => {
            const res = await accountsService.verifyEmail({
              email: formState.email,
              code,
            });
            gotAccessToken(res.accessToken);
          }}
          resendCode={async () => {
            await accountsService.requestVerificationCode({
              email: formState.email,
            });
          }}
        />
      )}
      {mode === 'success' && <LoadingDots />}
    </Dialog>
  );
});
